import officeConstants from '@/constant/office'

// Low-level Office.js probes shared by the office service modules. Everything here is
// a pure read against window.Office / window.PowerPoint — no store, router or API
// imports — so both index.js and resize.js can depend on it without a cycle.

// Feature-detect a PowerPoint JavaScript API requirement set. Never throws: on a host
// that does not expose `requirements` at all, callers just see "unsupported".
export const isPowerPointApiSupported = (version) => {
  try {
    return Boolean(window.Office?.context?.requirements?.isSetSupported('PowerPointApi', version))
  } catch (error) {
    return false
  }
}

// Writes one document.settings key and resolves only once Office has PERSISTED it into the
// document. The persistence guarantee is the point: AHA-46329's desktop insert path exports
// a slide immediately after writing this, and an export that ran before the save landed
// would carry the PREVIOUS value in the clone's settings snapshot — silently binding the
// clone to the wrong AhaSlides slide.
export const saveDocumentSetting = (key, value) => new Promise((resolve, reject) => {
  const settings = window.Office?.context?.document?.settings
  if (!settings) {
    reject(new Error('office/document.settings not available'))
    return
  }
  settings.set(key, value)
  settings.saveAsync((result) => {
    const failed = window.Office?.AsyncResultStatus
      && result?.status === window.Office.AsyncResultStatus.Failed
    if (failed) {
      reject(new Error(result?.error?.message || 'failed to save document setting'))
      return
    }
    resolve()
  })
})

// Reads one document.settings key, refreshing first so a value another frame wrote is not missed.
// Never rejects: resolves null on any failure, because its callers (AHA-46751's ownership check)
// treat "cannot read the binding" as a reason to fall back, not as an error to propagate.
//
// Deliberately NOT services/office/index.js's loadDataFromSlidePowerPoint, which this otherwise
// mirrors: that one also commits the Office platform into the store as a side effect, and lives
// in the module that imports this one — reusing it would mean an import cycle and a store write
// from a pure read.
export const readDocumentSetting = (key) => new Promise((resolve) => {
  const settings = window.Office?.context?.document?.settings
  if (!settings) {
    resolve(null)
    return
  }
  settings.refreshAsync((result) => {
    const failed = window.Office?.AsyncResultStatus
      && result?.status === window.Office.AsyncResultStatus.Failed
    if (failed) {
      resolve(null)
      return
    }
    const value = settings.get(key)
    resolve(value === undefined ? null : value)
  })
})

// Desktop hosts (Windows and Mac) as opposed to PowerPoint on the web. The distinction is
// load-bearing for AHA-46329: on desktop a cloned slide inherits a SNAPSHOT of the donor's
// document.settings taken at export time, so per-clone settings survive; on the web settings
// stay shared live across every frame and cannot tell clones apart at all.
export const isDesktopOfficeHost = () => {
  const platform = window.Office?.context?.platform
  return platform === officeConstants.PC || platform === officeConstants.MAC
}

// One-shot active-view query. A Common API, so unlike PowerPoint.run it still answers in the
// read (Slide Show) session — which is exactly what callers need it for: both the read-view
// binding bridge (index.js) and the frame resize (resize.js) have to know whether they are in
// the show, and the Rich API cannot tell them. Resolves 'edit' | 'read' | null; never rejects,
// so a host without the API just reads as "unknown" and callers fall through to their default.
export const queryActiveView = () => new Promise((resolve) => {
  const doc = window.Office?.context?.document
  if (typeof doc?.getActiveViewAsync !== 'function') {
    resolve(null)
    return
  }
  doc.getActiveViewAsync((result) => resolve((result && result.value) || null))
})

// Reads the currently-selected slide in the live show via getSelectedDataAsync(SlideRange).
// This is the only per-instance signal that tracks the slideshow position (incl. back-nav),
// and on add-in mount it is also how a frame learns which slide it lives on.
// Invokes onResult(selectedId, error, slides) — selectedId is null on failure.
export const readSelectedSlideId = (onResult) => {
  const doc = window.Office?.context?.document
  const slideRange = window.Office?.CoercionType?.SlideRange
  if (!doc || !doc.getSelectedDataAsync || !slideRange) {
    onResult(null, 'office/getSelectedDataAsync not available')
    return
  }
  doc.getSelectedDataAsync(slideRange, (result) => {
    const failed = window.Office?.AsyncResultStatus
      && result?.status === window.Office.AsyncResultStatus.Failed
    if (failed) {
      onResult(null, result?.error?.message || 'failed')
      return
    }
    const slides = result?.value?.slides
    const selectedId = slides && slides[0] && slides[0].id
    onResult(selectedId, null, slides)
  })
}

// AHA-43111: single-flight + hard-timeout wrapper around the same call, for the read (Slide Show)
// session on the web. Modelled on Mentimeter's wrapper, minus its retry layer.
//
// The measured behaviour: the read session serves getSelectedDataAsync in exactly ONE frame per
// document and leaves every other caller outstanding with no callback, ever (observed past 44s;
// five fresh retries over 40s never broke through). Two consequences follow, and these two layers
// address them:
//
//   TIMEOUT — the awaited call in a starved frame simply never settles, so everything sequenced
//   after it is dead code in that frame: the remaining cold-load fallbacks are never tried and the
//   frame sits on the presentation list forever. Failing in a bounded time turns a hang into a
//   fall-through. It is a HARD timeout only in the sense that it settles OUR promise; there is no
//   way to cancel an outstanding Office call, so the host may still answer later — hence
//   onLateAnswer, which hands that answer over instead of dropping it on the floor.
//
//   SINGLE-FLIGHT — every abandoned call stays outstanding for the life of the frame, so repeated
//   asks accumulate them without bound against a channel already proven unable to serve them. One
//   in flight per frame at a time. The cold-load path is the only caller and asks once, so this is
//   a guard rather than a hot-path optimisation: it bounds any future re-entry, and it is what lets
//   the slot be freed on settle instead of on the host callback that may never come.
//   (startSlideFocusPolling still calls the raw readSelectedSlideId every 250ms; it only runs in
//   edit view, where the host answers normally.)
//
// Retry is deliberately NOT part of this. Mentimeter has it, but their retry is shaped exactly like
// the starved-frame probe we already ran — abandon, wait, issue a fresh call — and ours exhausted
// without a single breakthrough. Retrying here would only postpone the fall-through it exists to
// enable. Cross-frame recovery is handled by broadcasting the winner's answer instead
// (see publishShowCurrentSlideId in services/office/ppt-online-current-slide).
export const SELECTED_SLIDE_READ_TIMEOUT_MS = 2500

let activeSelectedSlideRead = null

const readSelectedSlideIdWithTimeout = (timeoutMs, onLateAnswer) => new Promise((resolve, reject) => {
  let settled = false
  const timer = setTimeout(() => {
    settled = true
    reject(new Error(`office/getSelectedDataAsync did not answer within ${timeoutMs}ms`))
  }, timeoutMs)
  readSelectedSlideId((selectedId, error) => {
    clearTimeout(timer)
    if (settled) {
      // Answered after we gave up. Rare but real: the promise is long gone, so the value can
      // only be delivered out-of-band.
      if (onLateAnswer) onLateAnswer(error ? null : selectedId)
      return
    }
    settled = true
    if (error) {
      reject(new Error(error))
      return
    }
    resolve(selectedId)
  })
})

export const readSelectedSlideIdBounded = (
  timeoutMs = SELECTED_SLIDE_READ_TIMEOUT_MS,
  onLateAnswer = null,
) => {
  if (activeSelectedSlideRead) return activeSelectedSlideRead
  // Cleared on settle, not on the host callback: a timed-out slot must be reusable, otherwise one
  // starved call would block this frame from ever asking again.
  activeSelectedSlideRead = readSelectedSlideIdWithTimeout(timeoutMs, onLateAnswer).finally(() => {
    activeSelectedSlideRead = null
  })
  // The .finally() chain is what callers share, but its rejection must not surface as an unhandled
  // rejection when no caller happens to be awaiting that particular link.
  activeSelectedSlideRead.catch(() => {})
  return activeSelectedSlideRead
}
